import { useState, useEffect, Fragment } from 'react';
import { toast } from 'react-toastify';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useConfirm } from 'material-ui-confirm';

// Use unified date utils
import { moment } from '../utils/dateUtils';

import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Button,
  Typography,
  Divider,
  Select,
  MenuItem,
  FormControl,
  Grid,
  InputLabel,
  TextField,
  FormHelperText,
  Menu,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  Chip,
  Alert,
} from '@mui/material';

import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';

// Icons
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';
import AddIcon from '@mui/icons-material/Add';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import ContactPhoneIcon from '@mui/icons-material/ContactPhone';
import PlaceIcon from '@mui/icons-material/Place';
import SearchIcon from '@mui/icons-material/Search';

// Custom Components
import {
  Spinner,
  BrowserTitle,
  AppointmentAppointmentsTable,
  AppointmentProposalsTable,
  AppointmentClientsTable,
  PropertyClientsTable,
  PropertyClientsDrawer,
  PropertyMediaTable,
  PropertyArchiveTable,
  PropertyLegacyProposalsTable,
  PropertyEquipmentTable,
} from '../components';

// Utils
import messages from '../static/messages';
import { emptyStringClear } from '../utils/cleaners';

// Services
import appointmentsServices from '../services/appointmentsServices';
import propertiesServices from '../services/propertiesServices';
import userServices from '../services/userServices';
import clientsServices from '../services/clientsServices';
import placesServices from '../services/placesServices';
import integrationServices from '../services/integrationServices';

// Hooks
import useAccordion from '../hooks/useAccordion';
import useDrawer from '../hooks/useDrawer';

// Styles
import { button } from '../static/theme-styles';

// External variables
import { appointment_times } from '../components/Drawers/appointment.drawer';
import { base_client } from '../components/Tables/Property/property.clients';

const required_fields = ['streetNumber', 'streetName', 'city', 'state', 'zipCode'];

const blocks = ['details', 'appointments', 'proposals', 'equipment', 'clients', 'media'];

const menuPaperProps = {
  elevation: 0,
  sx: {
    left: '105px !important',
    overflow: 'visible',
    filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
    mt: 1.5,
    '& .MuiSvgIcon-root': {
      width: 25,
      height: 25,
      mr: 1,
    },
    '&:before': {
      content: '""',
      display: 'block',
      position: 'absolute',
      top: 0,
      left: 14,
      width: 10,
      height: 10,
      bgcolor: 'background.paper',
      transform: 'translateY(-50%) rotate(45deg)',
      zIndex: 0,
    },
  },
};

const addressFormat = (property) => {
  let label = '';


  if (property) {
    if (property?.unit) label = `${property?.address} (unit ${property?.unit})`;
    else label = property?.address;
  }

  return label;
};

const AppointmentCreate = () => {
  const [errors, setErrors] = useState({});
  const [appointment, setAppointment] = useState({
    streetNumber: '',
    streetName: '',
    city: '',
    state: '',
    zipCode: '',
    unit: '',
    clients: [],
  });
  const [techniciansList, setTechniciansList] = useState();
  const [types, setTypes] = useState([]);
  const [sources, setSources] = useState([]);
  const [propertyTypes, setPropertyTypes] = useState([]);
  const [currentClient, setCurrentClient] = useState();
  const [drawerMode, setDrawerMode] = useState('find');
  const [anchorEl, setAnchorEl] = useState(null);
  const openMore = Boolean(anchorEl);
  
  // Track original property clients to detect changes
  const [originalPropertyClients, setOriginalPropertyClients] = useState([]);
  
  // Address autocomplete states
  const [addressOptions, setAddressOptions] = useState([]);
  const [addressLoading, setAddressLoading] = useState(false);
  const [addressInputValue, setAddressInputValue] = useState('');
  
  // Google Places states
  const [googlePlacesActive, setGooglePlacesActive] = useState(false);
  const [googleOptions, setGoogleOptions] = useState([]);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [showGoogleConfirmDialog, setShowGoogleConfirmDialog] = useState(false);
  const [searchSource, setSearchSource] = useState('database'); // 'database' or 'google'
  const [sessionToken] = useState(() => placesServices.generateSessionToken());
  
  // Duplicate property detection states
  const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);
  const [duplicateProperty, setDuplicateProperty] = useState(null);
  const [pendingGoogleAddress, setPendingGoogleAddress] = useState(null);
  
  // Multi-unit building detection states
  const [showMultiUnitDialog, setShowMultiUnitDialog] = useState(false);
  const [existingUnits, setExistingUnits] = useState([]);
  const [existingProperties, setExistingProperties] = useState([]);

  const { accordionToggle } = useAccordion(blocks);
  const { drawer, openDrawer, foolCheck } = useDrawer(currentClient);
  moment.tz.setDefault('America/Los_Angeles');
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [searchParams] = useSearchParams();

  // Action functions
  const resetErrors = () => {
    required_fields.forEach((el) =>
      setErrors((prevState) => ({
        ...prevState,
        [el]: false,
      })),
    );
  };

  const clearForm = () => {
    setAppointment({
      streetNumber: '',
      streetName: '',
      city: '',
      state: '',
      zipCode: '',
      unit: '',
      clients: [],
    });
    setOriginalPropertyClients([]);
  };


  const onAddNewClient = () => {
    delete currentClient.isEdited;

    if (currentClient.client.id) {
      // If searching by ID (existing client)
      getClientInfo(currentClient.client.id, currentClient.relation);
    } else {
      // Adding new client data - preserve all fields explicitly
      const clientData = { ...currentClient.client };
      console.log('Adding new client - raw data:', JSON.stringify(clientData, null, 2));
      
      const newClient = {
        relation: currentClient.relation || '',
        client: {
          firstName: clientData.firstName || '',
          lastName: clientData.lastName || '',
          fullName: `${clientData.firstName || ''} ${clientData.lastName || ''}`.trim(),
          ...(clientData.email ? { email: clientData.email } : {}),
          ...(clientData.phoneNumber ? { phoneNumber: clientData.phoneNumber } : {}),
          ...(clientData.companyName ? { companyName: clientData.companyName } : {}),
          ...(clientData.mobile ? { mobile: clientData.mobile } : {}),
          ...(clientData.fax ? { fax: clientData.fax } : {}),
        },
      };
      
      console.log('New client object:', JSON.stringify(newClient, null, 2));

      setAppointment((prevState) => ({
        ...prevState,
        // Add to local clients array if no property, or to property.clients if property exists
        clients: appointment.property 
          ? prevState.clients
          : [...prevState.clients, newClient],
        property: appointment.property 
          ? {
              ...prevState.property,
              clients: [...(prevState.property.clients || []), newClient],
            }
          : prevState.property,
      }));
    }

    onCloseDrawer(resetErrors);
  };

  const onAddNewClientFromExisting = (existingClient, closeModal) => {
    delete currentClient.isEdited;
    
    console.log('Adding existing client:', JSON.stringify(existingClient, null, 2));

    const clientData = {
      relation: '',
      client: {
        id: existingClient.id,
        firstName: existingClient.firstName || '',
        lastName: existingClient.lastName || '',
        fullName: `${existingClient.firstName || ''} ${existingClient.lastName || ''}`.trim(),
        ...(existingClient.email ? { email: existingClient.email } : {}),
        ...(existingClient.phoneNumber ? { phoneNumber: existingClient.phoneNumber } : {}),
        ...(existingClient.companyName ? { companyName: existingClient.companyName } : {}),
      },
    };
    
    console.log('Client data object:', JSON.stringify(clientData, null, 2));

    setAppointment((prevState) => ({
      ...prevState,
      // Add to local clients array if no property, or to property.clients if property exists
      clients: appointment.property 
        ? prevState.clients
        : [...prevState.clients, clientData],
      property: appointment.property 
        ? {
            ...prevState.property,
            clients: [...(prevState.property.clients || []), clientData],
          }
        : prevState.property,
    }));

    onCloseDrawer(resetErrors);
    closeModal();
  };

  const onAddClient = () => {
    setCurrentClient({ ...base_client, isEdited: true });
    setDrawerMode('new');
    openDrawer();
  };

  const onFindClient = () => {
    setCurrentClient({ ...base_client, isEdited: true });
    setDrawerMode('find');
    openDrawer();
  };

  const onSubmit = async () => {
    if (appointment?.property) {
      // Check if clients were added to existing property
      const currentClients = appointment.property.clients || [];
      const hasNewClients = currentClients.length > originalPropertyClients.length;
      
      if (hasNewClients) {
        // Update property with new clients before creating appointment
        try {
          // Clean clients data for API
          const cleanedClients = currentClients.map(clientData => ({
            ...(clientData.id && { id: clientData.id }),
            relation: clientData.relation || '',
            client: {
              ...(clientData.client?.id && { id: clientData.client.id }),
              firstName: clientData.client?.firstName || '',
              lastName: clientData.client?.lastName || '',
              ...(clientData.client?.email ? { email: clientData.client.email } : {}),
              ...(clientData.client?.phoneNumber ? { phoneNumber: clientData.client.phoneNumber } : {}),
            }
          }));
          
          console.log('Updating property with new clients:', cleanedClients);
          await propertiesServices.update(appointment.property.id, { clients: cleanedClients });
          toast.success('Clients saved to property');
        } catch (error) {
          console.error('Failed to update property clients:', error);
          toast.error('Failed to save clients to property');
          return; // Stop if clients save failed
        }
      }
      
      createNewAppointment(appointment.property.id);
    } else {
      createNewProperty();
    }
  };

  // Handler functions
  const handleChange = (event) => {
    const key = event.target?.name || 'date';
    const val = event.target?.value;

    setAppointment((prevState) => ({
      ...prevState,
      [key]: event.target?.name ? val : moment(event.$d).format('MM/DD/YYYY'),
    }));
  };

  // Handle address search with debounce - first in database, then offer Google
  const handleAddressSearch = async (searchValue) => {
    if (!searchValue || searchValue.length < 2) {
      setAddressOptions([]);
      setGoogleOptions([]);
      setSearchSource('database');
      return;
    }

    // If currently searching in Google mode
    if (searchSource === 'google') {
      await handleGoogleSearch(searchValue);
      return;
    }

    // First, search in database
    setAddressLoading(true);
    
    try {
      const response = await propertiesServices.searchRequest(0, 20, searchValue, false);
      
      if (response.status === 206) {
        const results = response.data.data || [];
        setAddressOptions(results);
        
        // If no results found and search is long enough, show dialog to search in Google
        if (results.length === 0 && searchValue.length >= 3 && googlePlacesActive) {
          setShowGoogleConfirmDialog(true);
        }
      }
    } catch (error) {
      console.log('Address search error:', error);
      setAddressOptions([]);
    } finally {
      setAddressLoading(false);
    }
  };

  // Handle Google Places search
  const handleGoogleSearch = async (searchValue) => {
    if (!searchValue || searchValue.length < 3) {
      setGoogleOptions([]);
      return;
    }

    setGoogleLoading(true);
    
    try {
      const response = await placesServices.autocomplete(searchValue, sessionToken);
      setGoogleOptions(response.data || []);
    } catch (error) {
      console.log('Google Places search error:', error);
      setGoogleOptions([]);
      toast.error('Google Places search failed');
    } finally {
      setGoogleLoading(false);
    }
  };

  // Handle confirmation to search in Google
  const handleGoogleConfirm = () => {
    setShowGoogleConfirmDialog(false);
    setSearchSource('google');
    setAddressOptions([]);
    toast.info('Searching in Google Places...');
    handleGoogleSearch(addressInputValue);
  };

  // Handle cancel Google search dialog
  const handleGoogleCancel = () => {
    setShowGoogleConfirmDialog(false);
  };

  // Switch back to database search
  const handleSwitchToDatabase = () => {
    setSearchSource('database');
    setGoogleOptions([]);
    setAddressInputValue('');
  };

  // Handle Google Place selection
  const handleGooglePlaceSelect = async (event, place) => {
    console.log('=== handleGooglePlaceSelect CALLED ===');
    console.log('Place selected:', place);
    
    if (!place) {
      console.log('No place selected, returning');
      return;
    }

    setGoogleLoading(true);
    
    try {
      // Get full place details
      console.log('Fetching place details for placeId:', place.placeId);
      const response = await placesServices.getDetails(place.placeId, sessionToken);
      console.log('Place details response:', response);
      const details = response.data;
      
      if (details) {
        // Check for duplicate property in database BEFORE filling the form
        console.log('Google Place details:', details);
        
        const checkPayload = {
          streetNumber: details.streetNumber || '',
          streetName: details.streetName || '',
          city: details.city || '',
          state: details.state || '',
          zipCode: details.zipCode || '',
          unit: '', // Google doesn't provide unit
        };
        
        console.log('=== DUPLICATE CHECK ===');
        console.log('Sending to backend:', JSON.stringify(checkPayload, null, 2));
        
        const duplicateCheck = await propertiesServices.checkDuplicate(checkPayload);
        
        console.log('Backend response:', JSON.stringify(duplicateCheck.data, null, 2));
        
        if (duplicateCheck.data?.isDuplicate && duplicateCheck.data?.existingProperty) {
          // Found exact duplicate - show dialog to use existing property
          setDuplicateProperty(duplicateCheck.data.existingProperty);
          setPendingGoogleAddress(details);
          setShowDuplicateDialog(true);
          setGoogleLoading(false);
          return;
        }
        
        // Check if this is a multi-unit building requiring unit number
        if (duplicateCheck.data?.isMultiUnitBuilding && duplicateCheck.data?.requiresUnit) {
          // Fill form fields first
          setAppointment((prevState) => ({
            ...prevState,
            streetNumber: details.streetNumber || '',
            streetName: details.streetName || '',
            city: details.city || '',
            state: details.state || '',
            zipCode: details.zipCode || '',
            unit: '', // Keep empty - user must fill
          }));
          
          // Show multi-unit dialog
          setExistingUnits(duplicateCheck.data.existingUnits || []);
          setExistingProperties(duplicateCheck.data.existingProperties || []);
          setPendingGoogleAddress(details);
          setShowMultiUnitDialog(true);
          setGoogleLoading(false);
          
          // Clear search
          setAddressInputValue('');
          setGoogleOptions([]);
          setSearchSource('database');
          return;
        }
        
        // No duplicate, not multi-unit - fill form fields (except Unit)
        setAppointment((prevState) => ({
          ...prevState,
          streetNumber: details.streetNumber || '',
          streetName: details.streetName || '',
          city: details.city || '',
          state: details.state || '',
          zipCode: details.zipCode || '',
          unit: '', // Keep unit empty for manual entry
        }));
        
        // Clear search and switch back to database mode
        setAddressInputValue('');
        setGoogleOptions([]);
        setSearchSource('database');
        
        toast.success('Address filled from Google Places. Please verify and add Unit if needed.');
      }
    } catch (error) {
      console.log('Google Places details error:', error);
      toast.error('Failed to get address details');
    } finally {
      setGoogleLoading(false);
    }
  };

  // Handle using existing property (from duplicate dialog)
  const handleUseExistingProperty = async () => {
    if (duplicateProperty) {
      // Close dialog first
      setShowDuplicateDialog(false);
      
      // Load full property details with appointments history
      try {
        const response = await propertiesServices.getPropertyById(duplicateProperty.id);
        
        if (response.status === 200) {
          const fullProperty = response.data;
          
          // Set property with all its data (like selecting from database search)
          // User can continue filling appointment details
          setAppointment((prevState) => ({
            ...prevState,
            property: fullProperty,
            streetNumber: fullProperty.streetNumber || '',
            streetName: fullProperty.streetName || '',
            city: fullProperty.city || '',
            state: fullProperty.state || '',
            zipCode: fullProperty.zipCode || '',
            unit: fullProperty.unit || '',
          }));
          
          // Save original clients to track if new ones are added
          setOriginalPropertyClients(fullProperty.clients || []);
          
          const unitInfo = fullProperty.unit ? ` #${fullProperty.unit}` : '';
          toast.success(`Loaded existing property: ${fullProperty.streetNumber} ${fullProperty.streetName}${unitInfo}`);
        }
      } catch (error) {
        console.error('Error loading property:', error);
        toast.error('Failed to load property details');
      }
    }
    
    // Clear states
    setDuplicateProperty(null);
    setPendingGoogleAddress(null);
    setAddressInputValue('');
    setGoogleOptions([]);
    setSearchSource('database');
  };

  // Handle creating new property anyway (different unit)
  const handleCreateDifferentUnit = () => {
    if (pendingGoogleAddress) {
      // Fill form with Google address, user needs to enter different unit
      setAppointment((prevState) => ({
        ...prevState,
        streetNumber: pendingGoogleAddress.streetNumber || '',
        streetName: pendingGoogleAddress.streetName || '',
        city: pendingGoogleAddress.city || '',
        state: pendingGoogleAddress.state || '',
        zipCode: pendingGoogleAddress.zipCode || '',
        unit: '', // Keep unit empty - user must enter different unit
      }));
      
      toast.info('Please enter a Unit/Apt # to create a different property at this address.');
    }
    
    // Clear states
    setShowDuplicateDialog(false);
    setDuplicateProperty(null);
    setPendingGoogleAddress(null);
    setAddressInputValue('');
    setGoogleOptions([]);
    setSearchSource('database');
  };

  // Handle selecting existing unit from multi-unit dialog
  const handleSelectExistingUnit = async (property) => {
    console.log('Selected existing property:', property);
    
    // Close dialog
    setShowMultiUnitDialog(false);
    
    // Load full property details with appointments history
    try {
      const response = await propertiesServices.getPropertyById(property.id);
      
      if (response.status === 200) {
        const fullProperty = response.data;
        
        // Set property with all its data (like selecting from database search)
        setAppointment((prevState) => ({
          ...prevState,
          property: fullProperty,
          streetNumber: fullProperty.streetNumber || '',
          streetName: fullProperty.streetName || '',
          city: fullProperty.city || '',
          state: fullProperty.state || '',
          zipCode: fullProperty.zipCode || '',
          unit: fullProperty.unit || '',
        }));
        
        // Save original clients to track if new ones are added
        setOriginalPropertyClients(fullProperty.clients || []);
        
        toast.success(`Loaded property: ${fullProperty.streetNumber} ${fullProperty.streetName} #${fullProperty.unit}`);
      }
    } catch (error) {
      console.error('Error loading property:', error);
      toast.error('Failed to load property details');
    }
    
    // Clear states
    setExistingUnits([]);
    setExistingProperties([]);
    setPendingGoogleAddress(null);
    setAddressInputValue('');
    setGoogleOptions([]);
    setSearchSource('database');
  };

  // Handle cancel duplicate dialog
  const handleCancelDuplicate = () => {
    setShowDuplicateDialog(false);
    setDuplicateProperty(null);
    setPendingGoogleAddress(null);
  };

  // Handle property selection from autocomplete
  const handlePropertySelect = (event, property) => {
    if (property) {
      // Set property ID to trigger full data loading
      // Also extract address fields from property or place
      setAppointment((prevState) => ({
        ...prevState,
        property: { id: property.id },
        streetNumber: property.streetNumber || property.place?.addressNumber || '',
        streetName: property.streetName || property.place?.street || '',
        city: property.city || property.place?.municipality || '',
        state: property.state || property.place?.region?.substring(0, 2) || '',
        zipCode: property.zipCode || property.place?.postalCode?.split(' ')[0] || '',
        unit: property.unit || property.place?.unitNumber || '',
      }));
      
      // Clear address input
      setAddressInputValue('');
      
      // Full property data will be loaded by useEffect watching appointment.property?.id
    }
  };

  const handleClick = (event) => setAnchorEl(event.currentTarget);

  const handleClearForm = () => {
    confirm({
      title: messages.confirm_message.clear,
      description: messages.confirm_message.clear_description,
    })
      .then(() => clearForm())
      .catch(() => toast.info(messages.cancel_message));
  };

  const handleValidate = () => {
    let hasErrors = false;
    
    required_fields.forEach((field) => {
      if (appointment[field] === '' || appointment[field] === null || !appointment[field]) {
        setErrors((prevState) => ({
          ...prevState,
          [field]: true,
        }));
        hasErrors = true;
      } else {
        setErrors((prevState) => ({
          ...prevState,
          [field]: false,
        }));
      }
    });

    // Check if we have address (either from property or manually entered)
    if (hasErrors) {
      return toast.error('Please fill in all required address fields');
    }

    // Date and time are optional - they are only informational
    // The actual scheduling happens when appointment is moved from Queue via drag & drop

    // Client is now optional - can be added later
    // if (!appointment?.clients.length && !appointment?.property?.clients?.length)
    //   return toast.error('You must add at least one client');

    onSubmit();
  };

  // Async functions
  const onCloseDrawer = async (resetErrors) => {
    const confirmed = await foolCheck();

    if (confirmed === true) {
      setDrawerMode('find');
      setCurrentClient(null);
      resetErrors && resetErrors();
    }
  };


  const getPropertyData = async (id) => {
    try {
      const response = await propertiesServices.getPropertyById(id);
      let prop = response.data;

      // Check if Legacy CRM Notes already exist
      const hasLegacyNotes = prop.notes?.some(note => 
        note.note?.includes('[LEGACY CRM NOTES]')
      );

      // If no legacy notes and property has address, try to fetch them
      if (!hasLegacyNotes && prop.address) {
        try {
          const legacyResponse = await integrationServices.getLegacyPropertyNotes(prop.address);
          if (legacyResponse.data.found && legacyResponse.data.notes) {
            // Save legacy notes to property
            const legacyNote = `[LEGACY CRM NOTES]\n${legacyResponse.data.notes}`;
            await propertiesServices.addPropertyNote(id, legacyNote);
            
            // Reload property to get updated notes
            const updatedResponse = await propertiesServices.getPropertyById(id);
            if (updatedResponse.status === 200) {
              prop = updatedResponse.data;
              toast.success('📝 Legacy notes imported automatically');
            }
          }
        } catch (legacyError) {
          // Silent fail - legacy notes are optional
          console.log('No legacy notes found:', legacyError);
        }
      }

      // Show notes count to dispatcher
      if (prop.notes?.length > 0) {
        toast.info(`📝 Property has ${prop.notes.length} note(s)`);
      }

      const autoContact = prop.clients?.length === 1
        ? { id: prop.clients[0].client?.id }
        : null;

      setAppointment((prevState) => ({
        ...prevState,
        property: prop,
        ...(autoContact && !prevState.contactClient ? { contactClient: autoContact } : {}),
        streetNumber: prop.streetNumber || prop.place?.addressNumber || '',
        streetName: prop.streetName || prop.place?.street || '',
        city: prop.city || prop.place?.municipality || '',
        state: prop.state || prop.place?.region?.substring(0, 2) || '',
        zipCode: prop.zipCode || prop.place?.postalCode?.split(' ')[0] || '',
        unit: prop.unit || prop.place?.unitNumber || '',
      }));
      
      setOriginalPropertyClients(prop.clients || []);
    } catch (error) {
      toast.error(messages.errors.error_data_loaning);
      console.log(error);
    }
  };

  const getPropertyTypes = async () => {
    try {
      const response = await propertiesServices.getPropertyTypes();

      setPropertyTypes(response.data.data);
    } catch (error) {
      toast.error(messages.errors.error_data_loaning);
      console.log(error);
    }
  };

  const getTechniciansList = async () => {
    try {
      // Get all assignable users (technicians, admins, superadmins, dispatchers)
      const response = await userServices.getAssignableUsers(false);

      if (response.status === 206) {
        // Filter to only include roles that can be assigned to appointments
        const assignableRoles = ['technician', 'admin', 'superadmin', 'dispatcher'];
        const filteredUsers = response.data.data
          .filter((user) => assignableRoles.includes(user.role) && !user.deletedAt)
          .map((tech) => ({
            tech_id: tech.id,
            name: tech.fullName || `${tech.firstName} ${tech.lastName}`,
            color: tech.preferences?.color || 'red',
          }));
        setTechniciansList(filteredUsers);
      }
    } catch (error) {
      toast.error(messages.errors.error_data_loaning);
      console.log(error);
    }
  };

  const getTypes = async () => {
    try {
      const response = await appointmentsServices.getAppointmentTypes(false);

      if (response.status === 206) setTypes(response.data.data);
    } catch (error) {
      toast.error(messages.errors.error_data_loaning);
      console.log(error);
    }
  };

  const getSources = async () => {
    try {
      const response = await appointmentsServices.getAppointmentSources(false);

      if (response.status === 206) setSources(response.data.data);
    } catch (error) {
      toast.error(messages.errors.error_data_loaning);
      console.log(error);
    }
  };

  const getClientInfo = async (id, relation) => {
    try {
      const response = await clientsServices.getClientById(id);
      const client = response.data;
      
      console.log('Got client info from API:', JSON.stringify(client, null, 2));

      const clientData = {
        relation: relation || '',
        client: {
          id: client.id,
          firstName: client.firstName || '',
          lastName: client.lastName || '',
          fullName: client.fullName || `${client.firstName || ''} ${client.lastName || ''}`.trim(),
          ...(client.email ? { email: client.email } : {}),
          ...(client.phoneNumber ? { phoneNumber: client.phoneNumber } : {}),
          ...(client.companyName ? { companyName: client.companyName } : {}),
          ...(client.mobile ? { mobile: client.mobile } : {}),
        },
      };
      
      console.log('Client data to add:', JSON.stringify(clientData, null, 2));

      setAppointment((prevState) => ({
        ...prevState,
        // Add to local clients array if no property, or to property.clients if property exists
        clients: appointment.property 
          ? prevState.clients
          : [...prevState.clients, clientData],
        property: appointment.property 
          ? {
              ...prevState.property,
              clients: [...(prevState.property.clients || []), clientData],
            }
          : prevState.property,
      }));
    } catch (error) {
      toast.error(messages.errors.error_data_loaning);
      console.log(error);
    }
  };

  const createNewAppointment = async (propertyId) => {
    // NOTE: All appointments should go to Queue first
    // startTime and endTime are NOT set during creation
    // They will be set when appointment is moved from Queue to a specific time slot via drag & drop
    
    // Build preferred date/time if provided
    let preferredDate = null;
    let preferredStartTime = null;
    let preferredEndTime = null;
    
    if (appointment.date) {
      // Parse date in MM/DD/YYYY format
      preferredDate = moment(appointment.date, 'MM/DD/YYYY').toISOString();
      
      if (appointment.startAt) {
        // Combine date with start time
        preferredStartTime = moment(appointment.date, 'MM/DD/YYYY')
          .set({
            hour: parseInt(appointment.startAt.split(':')[0]),
            minute: parseInt(appointment.startAt.split(':')[1]),
            second: 0,
          })
          .toISOString();
      }
      
      if (appointment.finishAt) {
        // Combine date with end time
        preferredEndTime = moment(appointment.date, 'MM/DD/YYYY')
          .set({
            hour: parseInt(appointment.finishAt.split(':')[0]),
            minute: parseInt(appointment.finishAt.split(':')[1]),
            second: 0,
          })
          .toISOString();
      }
    }
    
    const data = {
      status: 'Active',
      ...(appointment.type && {
        type: {
          id: appointment.type,
        },
      }),
      ...(appointment.source && {
        source: {
          id: appointment.source,
        },
      }),
      // Note: technician (user) is also not set during creation
      // It will be assigned when appointment is dropped into a technician's column
      property: {
        id: propertyId,
      },
      ...(appointment.notes && {
        notes: [
          {
            note: appointment.notes,
          },
        ],
      }),
      // Add preferred date/time fields
      ...(preferredDate && { preferredDate }),
      ...(preferredStartTime && { preferredStartTime }),
      ...(preferredEndTime && { preferredEndTime }),
      // Add preferred technician
      ...(appointment.technician && {
        preferredUser: {
          id: appointment.technician,
        },
      }),
      ...(appointment.contactClient?.id && {
        contactClient: {
          id: appointment.contactClient.id,
        },
      }),
    };

    console.log('Creating appointment with data:', JSON.stringify(data, null, 2));

    try {
      const response = await appointmentsServices.create(data);

      if (response.status === 201) {
        toast.success(messages.appointment.create);
        navigate(`/appointments`);
      }
    } catch (error) {
      toast.error(messages.errors.error_try_again);
      console.log(error);
    }
  };

  const createNewProperty = async () => {
    // First, check for duplicate property BEFORE creating
    try {
      const duplicateCheck = await propertiesServices.checkDuplicate({
        streetNumber: appointment.streetNumber,
        streetName: appointment.streetName,
        city: appointment.city,
        state: appointment.state,
        zipCode: appointment.zipCode,
        unit: appointment.unit || '',
      });
      
      console.log('Duplicate check before save:', duplicateCheck.data);
      
      if (duplicateCheck.data?.isDuplicate && duplicateCheck.data?.existingProperty) {
        // Found duplicate - show dialog to use existing property
        setDuplicateProperty(duplicateCheck.data.existingProperty);
        setPendingGoogleAddress({
          streetNumber: appointment.streetNumber,
          streetName: appointment.streetName,
          city: appointment.city,
          state: appointment.state,
          zipCode: appointment.zipCode,
        });
        setShowDuplicateDialog(true);
        return; // Stop here, don't create property
      }
      
      // Check if this is a multi-unit building requiring unit number
      if (duplicateCheck.data?.isMultiUnitBuilding && duplicateCheck.data?.requiresUnit) {
        setExistingUnits(duplicateCheck.data.existingUnits || []);
        setExistingProperties(duplicateCheck.data.existingProperties || []);
        setPendingGoogleAddress({
          streetNumber: appointment.streetNumber,
          streetName: appointment.streetName,
          city: appointment.city,
          state: appointment.state,
          zipCode: appointment.zipCode,
        });
        setShowMultiUnitDialog(true);
        return; // Stop here, require unit input
      }
    } catch (error) {
      console.log('Duplicate check error:', error);
      // Continue with creation if check fails
    }

    // Form full address from separate fields
    const fullAddress = `${appointment.streetNumber} ${appointment.streetName}, ${appointment.city}, ${appointment.state}, ${appointment.zipCode}`;
    
    // Get clients from either appointment.clients or appointment.property.clients
    const clientsList = appointment.property?.clients || appointment.clients || [];
    
    // Clean clients - pass only required fields to avoid validation errors
    const cleanedClients = clientsList.map(clientData => ({
      // Only include id for existing ClientProperty records
      ...(clientData.id && { id: clientData.id }),
      relation: clientData.relation || '',
      client: {
        // Only include id for existing clients
        ...(clientData.client?.id && { id: clientData.client.id }),
        firstName: clientData.client?.firstName || '',
        lastName: clientData.client?.lastName || '',
        // Only include email if it's a non-empty string
        ...(clientData.client?.email ? { email: clientData.client.email } : {}),
        // Only include phone if it's a non-empty string
        ...(clientData.client?.phoneNumber ? { phoneNumber: clientData.client.phoneNumber } : {}),
      }
    }));
    
    console.log('Creating property with clients:', cleanedClients);
    
    const property = {
      typeName: appointment.typeName,
      address: fullAddress,
      streetNumber: appointment.streetNumber,
      streetName: appointment.streetName,
      city: appointment.city,
      state: appointment.state,
      zipCode: appointment.zipCode,
      ...(appointment.unit && { unit: appointment.unit }),
      clients: cleanedClients,
    };

    try {
      const response = await propertiesServices.create(property);

      if (response.status === 200) createNewAppointment(response.data.id);
    } catch (error) {
      toast.error(messages.errors.error_try_again);
      console.log(error);
    }
  };

  // Hooks
  useEffect(() => {
    accordionToggle('details');
    getTechniciansList();
    getTypes();
    getSources();
    getPropertyTypes();
    resetErrors();
    
    // Check if Google Places integration is active
    const checkGooglePlaces = async () => {
      try {
        const response = await placesServices.getStatus();
        setGooglePlacesActive(response.data?.active || false);
      } catch (error) {
        console.log('Google Places status check failed:', error);
        setGooglePlacesActive(false);
      }
    };
    checkGooglePlaces();

    // Auto-load property if propertyId is passed via URL query params
    const propertyIdFromUrl = searchParams.get('propertyId');
    if (propertyIdFromUrl) {
      setAppointment((prev) => ({
        ...prev,
        property: { id: propertyIdFromUrl },
      }));
    }
  }, []);


  useEffect(() => {
    if (appointment.property?.id) {
      getPropertyData(appointment.property?.id);
    }
  }, [appointment.property?.id]);

  // Debounce address search (database or Google based on searchSource)
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (addressInputValue) {
        handleAddressSearch(addressInputValue);
      }
    }, 500); // 500ms debounce

    return () => clearTimeout(timeoutId);
  }, [addressInputValue, searchSource]);

  if (!techniciansList || !types || !sources) return <Spinner />;

  return (
    <Fragment>
      <Box component="section">
        <BrowserTitle title={`Create Appointment | Airmaxx Pro`} />

        <Button
          variant="text"
          onClick={() => {
            // Preserve the selected date from sessionStorage
            const savedDate = sessionStorage.getItem('appointments_date');
            const savedView = sessionStorage.getItem('appointments_view');
            const params = new URLSearchParams();
            if (savedDate) params.set('date', savedDate);
            if (savedView) params.set('view', savedView);
            const queryString = params.toString();
            navigate(`/appointments${queryString ? '?' + queryString : ''}`);
          }}
          startIcon={<KeyboardBackspaceIcon />}
          sx={{ mb: 2 }}
        >
          Back to Appointments
        </Button>

        {/* Appointment Details */}
        <Accordion
          defaultExpanded
          onChange={() => accordionToggle('details')}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="body1">Appointment Details</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              {/* Address Autocomplete Search */}
              {!appointment?.property?.id && (
                <Grid item xs={12}>
                  {/* Search source indicator */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Chip
                      icon={<SearchIcon />}
                      label="Database"
                      color={searchSource === 'database' ? 'primary' : 'default'}
                      variant={searchSource === 'database' ? 'filled' : 'outlined'}
                      onClick={() => handleSwitchToDatabase()}
                      size="small"
                    />
                    {googlePlacesActive && (
                      <Chip
                        icon={<PlaceIcon />}
                        label="Google Places"
                        color={searchSource === 'google' ? 'primary' : 'default'}
                        variant={searchSource === 'google' ? 'filled' : 'outlined'}
                        onClick={() => {
                          setSearchSource('google');
                          setAddressOptions([]);
                          if (addressInputValue.length >= 3) {
                            handleGoogleSearch(addressInputValue);
                          }
                        }}
                        size="small"
                      />
                    )}
                  </Box>

                  {/* Database Search Autocomplete */}
                  {searchSource === 'database' && (
                    <Autocomplete
                      options={addressOptions}
                      loading={addressLoading}
                      inputValue={addressInputValue}
                      onInputChange={(event, newInputValue, reason) => {
                        // Don't clear input on reset (component mount/unmount)
                        if (reason === 'reset' && !newInputValue && addressInputValue) {
                          return;
                        }
                        setAddressInputValue(newInputValue);
                      }}
                      onChange={handlePropertySelect}
                      getOptionLabel={(option) => {
                        // Use direct fields or fallback to place data
                        const streetNum = option.streetNumber || option.place?.addressNumber || '';
                        const streetName = option.streetName || option.place?.street || '';
                        const city = option.city || option.place?.municipality || '';
                        const state = option.state || option.place?.region?.substring(0, 2) || '';
                        const zip = option.zipCode || option.place?.postalCode?.split(' ')[0] || '';
                        const unit = option.unit || option.place?.unitNumber || '';
                        
                        if (streetNum || streetName) {
                          let label = `${streetNum} ${streetName}`.trim();
                          if (unit) label += ` #${unit}`;
                          if (city || state || zip) label += `, ${city}, ${state} ${zip}`;
                          return label;
                        }
                        // Fallback to place.label if available
                        return option.place?.label || option.address || '';
                      }}
                      isOptionEqualToValue={(option, value) => option.id === value.id}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="🔍 Search existing address..."
                          placeholder="Start typing street number or name..."
                          helperText={googlePlacesActive 
                            ? "Type to search existing properties. If not found, you can search in Google Places."
                            : "Type to search existing properties by address"
                          }
                        />
                      )}
                      renderOption={(props, option) => {
                        const streetNum = option.streetNumber || option.place?.addressNumber || '';
                        const streetName = option.streetName || option.place?.street || '';
                        const city = option.city || option.place?.municipality || '';
                        const state = option.state || option.place?.region?.substring(0, 2) || '';
                        const zip = option.zipCode || option.place?.postalCode?.split(' ')[0] || '';
                        const unit = option.unit || option.place?.unitNumber || '';
                        
                        return (
                          <li {...props} key={option.id}>
                            <Box>
                              <Typography variant="body1">
                                {streetNum} {streetName}
                                {unit && ` #${unit}`}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {city}, {state} {zip}
                              </Typography>
                            </Box>
                          </li>
                        );
                      }}
                      noOptionsText={addressInputValue.length < 2 ? "Type at least 2 characters..." : "No properties found in database"}
                      fullWidth
                    />
                  )}

                  {/* Google Places Search Autocomplete */}
                  {searchSource === 'google' && (
                    <Autocomplete
                      options={googleOptions}
                      loading={googleLoading}
                      inputValue={addressInputValue}
                      onInputChange={(event, newInputValue, reason) => {
                        // Don't clear input on reset (component mount/unmount)
                        if (reason === 'reset' && !newInputValue && addressInputValue) {
                          return;
                        }
                        setAddressInputValue(newInputValue);
                        if (newInputValue.length >= 3) {
                          handleGoogleSearch(newInputValue);
                        }
                      }}
                      onChange={handleGooglePlaceSelect}
                      getOptionLabel={(option) => option.description || ''}
                      isOptionEqualToValue={(option, value) => option.placeId === value.placeId}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="🌐 Search address in Google Places..."
                          placeholder="Start typing address..."
                          helperText="Search for new addresses using Google Places. Unit/Apt # field will remain empty for manual entry."
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {googleLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                      renderOption={(props, option) => (
                        <li {...props} key={option.placeId}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <PlaceIcon sx={{ color: '#4285F4', fontSize: 20 }} />
                            <Box>
                              <Typography variant="body1">{option.mainText}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {option.secondaryText}
                              </Typography>
                            </Box>
                          </Box>
                        </li>
                      )}
                      noOptionsText={addressInputValue.length < 3 ? "Type at least 3 characters..." : "No addresses found"}
                      fullWidth
                    />
                  )}
                </Grid>
              )}

              <Grid item xs={6}>
                <TextField
                  required
                  label="Street Number"
                  name="streetNumber"
                  value={appointment?.property?.streetNumber ? appointment?.property?.streetNumber || '' : appointment.streetNumber || ''}
                  onChange={(e) => {
                    e.target.value = e.target.value.replace(/\s/g, '');
                    handleChange(e);
                  }}
                  fullWidth
                  disabled={!!appointment?.property?.id}
                  error={errors.streetNumber}
                  helperText={errors.streetNumber && messages.errors.required_field}
                />
              </Grid>

              <Grid item xs={6}>
                <TextField
                  required
                  label="Street Name"
                  name="streetName"
                  value={appointment?.property?.streetName ? appointment?.property?.streetName || '' : appointment.streetName || ''}
                  onChange={handleChange}
                  fullWidth
                  disabled={!!appointment?.property?.id}
                  error={errors.streetName}
                  helperText={errors.streetName && messages.errors.required_field}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Unit / Apt #"
                  name="unit"
                  value={appointment?.property?.unit ? appointment?.property?.unit || '' : appointment.unit || ''}
                  onChange={handleChange}
                  fullWidth
                  disabled={!!appointment?.property?.id}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  label="City"
                  name="city"
                  value={appointment?.property?.city ? appointment?.property?.city || '' : appointment.city || ''}
                  onChange={handleChange}
                  fullWidth
                  disabled={!!appointment?.property?.id}
                  error={errors.city}
                  helperText={errors.city && messages.errors.required_field}
                />
              </Grid>

              <Grid item xs={6} sm={3}>
                <TextField
                  required
                  label="State"
                  name="state"
                  value={appointment?.property?.state ? appointment?.property?.state || '' : appointment.state || ''}
                  onChange={(event) => {
                    const e = { target: { name: 'state', value: event.target.value.toUpperCase() } };
                    handleChange(e);
                  }}
                  fullWidth
                  inputProps={{ maxLength: 2, style: { textTransform: 'uppercase' } }}
                  disabled={!!appointment?.property?.id}
                  error={errors.state}
                  helperText={errors.state && messages.errors.required_field}
                />
              </Grid>

              <Grid item xs={6} sm={3}>
                <TextField
                  required
                  label="ZIP Code"
                  name="zipCode"
                  value={appointment?.property?.zipCode ? appointment?.property?.zipCode || '' : appointment.zipCode || ''}
                  onChange={handleChange}
                  fullWidth
                  inputProps={{ maxLength: 10 }}
                  disabled={!!appointment?.property?.id}
                  error={errors.zipCode}
                  helperText={errors.zipCode && messages.errors.required_field}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel id="typeName" error={errors.typeName}>
                    Property Type
                  </InputLabel>
                  <Select
                    labelId="typeName"
                    name="typeName"
                    value={
                      appointment?.property?.typeName
                        ? appointment?.property?.typeName || ''
                        : appointment?.typeName || ''
                    }
                    label="Property Type"
                    // required
                    onChange={handleChange}
                    disabled={!!appointment?.property?.id}
                    // error={errors.typeName}
                  >
                    {propertyTypes?.map((type) => (
                      <MenuItem key={type.id} value={type.name}>
                        {type.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {/* {errors.typeName && (
                    <FormHelperText sx={{ color: '#ef5350' }}>{messages.errors.required_field}</FormHelperText>
                  )} */}
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel id="technician" error={errors.technician}>
                    Preferred Technician
                  </InputLabel>
                  <Select
                    labelId="technician"
                    name="technician"
                    value={appointment?.technician || ''}
                    label="Preferred Technician"
                    onChange={handleChange}
                    error={errors.technician}
                  >
                    {techniciansList?.map((tech) => (
                      <MenuItem key={tech.tech_id} value={tech.tech_id}>
                        {tech.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.technician && (
                    <FormHelperText sx={{ color: '#ef5350' }}>{messages.errors.required_field}</FormHelperText>
                  )}
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <Autocomplete
                  options={types.filter(type => type.name !== 'Day Off')}
                  getOptionLabel={(option) => option.name || ''}
                  value={types.find(t => t.id === appointment?.type) || null}
                  onChange={(event, newValue) => {
                    setAppointment((prevState) => ({
                      ...prevState,
                      type: newValue?.id || '',
                    }));
                  }}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Appointment Type *"
                      required
                      error={errors.type}
                      helperText={errors.type && messages.errors.required_field}
                    />
                  )}
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Autocomplete
                  options={sources}
                  getOptionLabel={(option) => option.name || ''}
                  value={sources.find(s => s.id === appointment?.source) || null}
                  onChange={(event, newValue) => {
                    setAppointment((prevState) => ({
                      ...prevState,
                      source: newValue?.id || '',
                    }));
                  }}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Source"
                    />
                  )}
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DesktopDatePicker
                    label="Preferred Date"
                    inputFormat="MM/DD/YYYY"
                    value={appointment.date || null}
                    onChange={handleChange}
                    disablePast
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        name="date"
                        fullWidth
                        error={false}
                        inputProps={{ ...params.inputProps, readOnly: true }}
                        helperText="Customer's preferred date"
                      />
                    )}
                    disabled={!appointment.property?.id && (!appointment.streetNumber || !appointment.streetName || !appointment.city || !appointment.state || !appointment.zipCode)}
                  />
                </LocalizationProvider>
              </Grid>

              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel id="startAt">
                    Preferred Start Time
                  </InputLabel>
                  <Select
                    labelId="startAt"
                    name="startAt"
                    value={appointment?.startAt || ''}
                    label="Preferred Start Time"
                    onChange={handleChange}
                    disabled={!appointment.date}
                  >
                    {appointment_times.map((time, index) => (
                      <MenuItem
                        key={index}
                        value={time.time24}
                        disabled={
                          appointment.finishAt &&
                          appointment_times.indexOf(time) >
                            appointment_times.findIndex((x) => x.time24 === appointment.finishAt) - 4
                        }
                      >
                        {time.time12}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>Customer's preferred start time</FormHelperText>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel id="finishAt">
                    Preferred End Time
                  </InputLabel>
                  <Select
                    labelId="finishAt"
                    name="finishAt"
                    value={appointment?.finishAt || ''}
                    label="Preferred End Time"
                    onChange={handleChange}
                    disabled={!appointment.date}
                  >
                    {appointment_times.map((time, index) => (
                      <MenuItem
                        key={index}
                        value={time.time24}
                        disabled={
                          appointment_times.indexOf(time) <
                          appointment_times.findIndex((x) => x.time24 === appointment.startAt) + 4
                        }
                      >
                        {time.time12}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>Customer's preferred end time</FormHelperText>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={appointment.property ? 6 : 12}>
                <TextField
                  name="notes"
                  label="Appointment Notes"
                  value={appointment?.notes}
                  rows={4}
                  onChange={handleChange}
                  fullWidth
                  multiline
                  // required
                  // error={errors.notes}
                  // helperText={errors.notes && messages.errors.required_field}
                />
              </Grid>

              {appointment.property && (
                <Grid item xs={12} md={6}>
                  <Box>
                    <Typography sx={{ fontWeight: 600, mb: 1 }}>
                      📝 Property Notes {appointment.property?.notes?.length > 0 && `(${appointment.property.notes.length})`}:
                    </Typography>

                    <Box sx={{ maxHeight: 300, overflow: 'auto', backgroundColor: '#f9f9f9', borderRadius: 1, p: 1 }}>
                      {appointment.property?.notes?.length > 0 ? (
                        appointment.property.notes.map((el) => (
                          <Box 
                            key={el.id} 
                            sx={{ 
                              mb: 1.5, 
                              p: 1.5, 
                              backgroundColor: el.note?.includes('[LEGACY CRM NOTES]') ? '#fff3e0' : '#fff',
                              borderRadius: 1,
                              border: el.note?.includes('[LEGACY CRM NOTES]') ? '1px solid #ffcc80' : '1px solid #e0e0e0',
                            }}
                          >
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                whiteSpace: 'pre-wrap',
                                fontWeight: el.note?.includes('[LEGACY CRM NOTES]') ? 500 : 400,
                              }}
                            >
                              {el.note}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#666', mt: 0.5, display: 'block' }}>
                              {el.createdBy?.fullName} • {moment(el.createdAt).format('L')}
                            </Typography>
                          </Box>
                        ))
                      ) : (
                        <Typography variant="body2" sx={{ color: '#999', textAlign: 'center', py: 2 }}>
                          No property notes
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Grid>
              )}
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Clients */}
        <Accordion
          defaultExpanded
          onChange={() => accordionToggle('clients')}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="body1">
                Clients
                {(appointment?.clients?.length > 0 || appointment?.property?.clients?.length > 0) && (
                  <Typography component="span" sx={{ ml: 1, color: 'primary.main', fontWeight: 'bold' }}>
                    ({appointment?.property?.clients?.length || appointment?.clients?.length || 0})
                  </Typography>
                )}
              </Typography>
              {/* Add Client button - positioned right after "Clients" text */}
              {(appointment.property?.id || (appointment.streetNumber && appointment.streetName && appointment.city && appointment.state && appointment.zipCode)) && (
                <Button 
                  variant="contained" 
                  color="primary"
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleClick(e);
                  }} 
                  startIcon={<AddIcon />}
                >
                  Add Client
                </Button>
              )}
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            {/* Show hint when address is not filled */}
            {!(appointment.property?.id || (appointment.streetNumber && appointment.streetName && appointment.city && appointment.state && appointment.zipCode)) && (
              <Box sx={{ 
                mb: 2, 
                p: 2, 
                bgcolor: 'info.light', 
                borderRadius: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}>
                <Typography variant="body2" color="info.contrastText">
                  Fill in the address fields above to add clients to this appointment.
                </Typography>
              </Box>
            )}

            {/* Menu for Add Client options */}
            <Menu
              anchorEl={anchorEl}
              id="account-menu"
              open={openMore}
              onClose={() => setAnchorEl(null)}
              onClick={() => setAnchorEl(null)}
              PaperProps={menuPaperProps}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
              <MenuItem onClick={onFindClient}>
                <PersonSearchIcon /> Existing Client
              </MenuItem>
              <MenuItem onClick={onAddClient}>
                <PersonAddIcon /> New Client
              </MenuItem>
            </Menu>

            {/* Contact Client Selector */}
            {(() => {
              const clients = appointment.property?.clients || appointment.clients || [];
              if (clients.length > 0) {
                return (
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel id="contactClient-label">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        Primary Contact
                      </Box>
                    </InputLabel>
                    <Select
                      labelId="contactClient-label"
                      value={appointment.contactClient?.id || ''}
                      label="Primary Contact"
                      onChange={(e) => {
                        const clientId = e.target.value;
                        setAppointment((prev) => ({
                          ...prev,
                          contactClient: clientId ? { id: clientId } : null,
                        }));
                      }}
                      startAdornment={<ContactPhoneIcon sx={{ mr: 1, color: '#1976d2' }} />}
                    >
                      <MenuItem value="">
                        <em>Auto (first client)</em>
                      </MenuItem>
                      {clients.map((cp) => (
                        <MenuItem key={cp.client?.id || cp.id} value={cp.client?.id || cp.id}>
                          {cp.client?.fullName || `${cp.client?.firstName || ''} ${cp.client?.lastName || ''}`.trim()}
                          {cp.relation ? ` (${cp.relation})` : ''}
                          {cp.client?.phoneNumber ? ` — ${cp.client.phoneNumber}` : ''}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>Select which client to contact for this appointment (Call/SMS)</FormHelperText>
                  </FormControl>
                );
              }
              return null;
            })()}

            {/* Always use AppointmentClientsTable to avoid API calls during creation */}
            {appointment.property && appointment.property.clients ? (
              <AppointmentClientsTable clients={appointment.property.clients || []} />
            ) : (
              <AppointmentClientsTable clients={appointment?.clients || []} />
            )}
          </AccordionDetails>
        </Accordion>

        {/* Appointments */}
        {appointment?.property?.appointments && (
          <Accordion
            defaultExpanded
            onChange={() => accordionToggle('appointments')}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="body1">Appointments</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <AppointmentAppointmentsTable appointments={appointment?.property?.appointments} />
            </AccordionDetails>
          </Accordion>
        )}


        {/* Archive (Legacy Appointments) */}
        {appointment?.property?.id && (
          <Accordion
            defaultExpanded={false}
            onChange={() => accordionToggle('archive')}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body1">Archive (Legacy Appointments)</Typography>
                <Chip 
                  label={appointment?.property?.archiveAppointments?.length || 0} 
                  size="small" 
                  color={appointment?.property?.archiveAppointments?.length > 0 ? "warning" : "default"}
                  sx={{ height: 20, fontSize: '0.75rem' }}
                />
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              {appointment?.property?.archiveAppointments?.length > 0 ? (
                <PropertyArchiveTable appointments={appointment.property.archiveAppointments} />
              ) : (
                <Typography variant="body2" color="text.secondary">No legacy appointments found</Typography>
              )}
            </AccordionDetails>
          </Accordion>
        )}

        {/* Legacy Proposals */}
        {appointment?.property?.id && (
          <Accordion
            defaultExpanded={false}
            onChange={() => accordionToggle('legacyProposals')}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body1">Legacy Proposals</Typography>
                <Chip 
                  label={appointment?.property?.legacyProposals?.length || 0} 
                  size="small" 
                  color={appointment?.property?.legacyProposals?.length > 0 ? "info" : "default"}
                  sx={{ height: 20, fontSize: '0.75rem' }}
                />
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              {appointment?.property?.legacyProposals?.length > 0 ? (
                <PropertyLegacyProposalsTable legacyProposals={appointment.property.legacyProposals} />
              ) : (
                <Typography variant="body2" color="text.secondary">No legacy proposals found</Typography>
              )}
            </AccordionDetails>
          </Accordion>
        )}

        {/* Proposals */}
        {appointment?.property?.proposals && (
          <Accordion
            defaultExpanded
            onChange={() => accordionToggle('proposals')}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="body1">Proposals</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <AppointmentProposalsTable proposals={appointment?.property?.proposals} />
            </AccordionDetails>
          </Accordion>
        )}

        {/* Installed Equipment */}
        {appointment?.property?.id && (
          <Accordion
            defaultExpanded={false}
            onChange={() => accordionToggle('equipment')}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body1">Installed Equipment</Typography>
                <Chip 
                  label={appointment?.property?.equipment?.length || 0} 
                  size="small" 
                  color={appointment?.property?.equipment?.length > 0 ? "primary" : "default"}
                  sx={{ height: 20, fontSize: '0.75rem' }}
                />
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <PropertyEquipmentTable
                equipment={appointment?.property?.equipment || []}
                propertyId={appointment?.property?.id}
                proposals={appointment?.property?.proposals || []}
                refresh={() => getPropertyData(appointment.property.id)}
              />
            </AccordionDetails>
          </Accordion>
        )}

        {/* Media */}
        {appointment?.property?.media && (
          <Accordion
            defaultExpanded
            onChange={() => accordionToggle('media')}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="body1">Property Media</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <PropertyMediaTable
                mediaList={appointment?.property.media}
                id={appointment.property.id}
                refresh={() => getPropertyData(appointment.property.id)}
              />
            </AccordionDetails>
          </Accordion>
        )}

        <Box sx={{ mt: 2 }}>
          <Button onClick={handleValidate} variant="contained" sx={button('primary', 'secondary')}>
            Save Appointment
          </Button>
          <Button onClick={handleClearForm} variant="contained" sx={{ ...button('secondary'), ml: 2 }}>
            Clear Form
          </Button>
        </Box>
      </Box>

      <PropertyClientsDrawer
        drawer={drawer}
        onCloseDrawer={onCloseDrawer}
        drawerMode={drawerMode}
        currentClient={currentClient}
        setCurrentClient={setCurrentClient}
        addNewClient={onAddNewClient}
        updateClient={onAddNewClient}
        onAddNewClientFromExisting={onAddNewClientFromExisting}
      />

      {/* Google Places Confirmation Dialog */}
      <Dialog
        open={showGoogleConfirmDialog}
        onClose={handleGoogleCancel}
        aria-labelledby="google-places-dialog-title"
        aria-describedby="google-places-dialog-description"
      >
        <DialogTitle id="google-places-dialog-title" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PlaceIcon sx={{ color: '#4285F4' }} />
          Address Not Found in Database
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="google-places-dialog-description">
            The address <strong>"{addressInputValue}"</strong> was not found in the existing properties database.
            <br /><br />
            Would you like to search for this address using Google Places?
            <br /><br />
            <Typography variant="caption" color="text.secondary">
              Note: A new property will be created when you save the appointment.
            </Typography>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleGoogleCancel} color="inherit">
            Cancel
          </Button>
          <Button 
            onClick={handleGoogleConfirm} 
            variant="contained" 
            startIcon={<PlaceIcon />}
            sx={{ bgcolor: '#4285F4', '&:hover': { bgcolor: '#3367D6' } }}
          >
            Search in Google Places
          </Button>
        </DialogActions>
      </Dialog>

      {/* Duplicate Property Found Dialog */}
      <Dialog
        open={showDuplicateDialog}
        onClose={handleCancelDuplicate}
        maxWidth="sm"
        fullWidth
        aria-labelledby="duplicate-dialog-title"
        aria-describedby="duplicate-dialog-description"
      >
        <DialogTitle id="duplicate-dialog-title" sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: '#fff3e0' }}>
          <Box sx={{ 
            bgcolor: '#ff9800', 
            borderRadius: '50%', 
            width: 32, 
            height: 32, 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center' 
          }}>
            ⚠️
          </Box>
          <Typography variant="h6">Address Already Exists</Typography>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <DialogContentText id="duplicate-dialog-description" component="div">
            <Typography variant="body1" gutterBottom>
              This address already exists in the database:
            </Typography>
            
            {duplicateProperty && (
              <Box sx={{ 
                bgcolor: '#f5f5f5', 
                p: 2, 
                borderRadius: 1, 
                my: 2,
                border: '1px solid #e0e0e0'
              }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  📍 {duplicateProperty.streetNumber} {duplicateProperty.streetName}
                  {duplicateProperty.unit && ` #${duplicateProperty.unit}`}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {duplicateProperty.city}, {duplicateProperty.state} {duplicateProperty.zipCode}
                </Typography>
                
                {/* Show property stats */}
                <Box sx={{ mt: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  {duplicateProperty.appointments?.length > 0 && (
                    <Chip 
                      size="small" 
                      label={`${duplicateProperty.appointments.length} appointments`}
                      color="primary"
                      variant="outlined"
                    />
                  )}
                  {duplicateProperty.proposals?.length > 0 && (
                    <Chip 
                      size="small" 
                      label={`${duplicateProperty.proposals.length} proposals`}
                      color="secondary"
                      variant="outlined"
                    />
                  )}
                  {duplicateProperty.clients?.length > 0 && (
                    <Chip 
                      size="small" 
                      label={`${duplicateProperty.clients.length} clients`}
                      color="info"
                      variant="outlined"
                    />
                  )}
                </Box>
              </Box>
            )}
            
            <Typography variant="body2" sx={{ mt: 2 }}>
              Using the existing property will preserve all appointment history.
            </Typography>
            
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              If this is a different unit (e.g., Apt #2), click "Different Unit" to create a new property.
            </Typography>
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={handleCancelDuplicate} color="inherit">
            Cancel
          </Button>
          <Button 
            onClick={handleCreateDifferentUnit} 
            variant="outlined"
            color="warning"
          >
            Different Unit
          </Button>
          <Button 
            onClick={handleUseExistingProperty} 
            variant="contained" 
            color="success"
          >
            Use Existing Property
          </Button>
        </DialogActions>
      </Dialog>

      {/* Multi-Unit Building Dialog - Requires Unit Number */}
      <Dialog
        open={showMultiUnitDialog}
        onClose={() => setShowMultiUnitDialog(false)}
        maxWidth="sm"
        fullWidth
        aria-labelledby="multiunit-dialog-title"
        aria-describedby="multiunit-dialog-description"
      >
        <DialogTitle id="multiunit-dialog-title" sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: '#e3f2fd' }}>
          <Box sx={{ 
            bgcolor: '#2196f3', 
            borderRadius: '50%', 
            width: 32, 
            height: 32, 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            color: 'white',
            fontSize: '16px'
          }}>
            🏢
          </Box>
          <Typography variant="h6">Multi-Unit Building Detected</Typography>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <DialogContentText id="multiunit-dialog-description" component="div">
            <Typography variant="body1" gutterBottom>
              This address appears to be a <strong>multi-unit building</strong>.
            </Typography>
            
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Records with unit numbers already exist at this address:
            </Typography>
            
            <Box sx={{ 
              bgcolor: '#f5f5f5', 
              p: 2, 
              borderRadius: 1, 
              my: 2,
              border: '1px solid #e0e0e0'
            }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                📍 {pendingGoogleAddress?.streetNumber} {pendingGoogleAddress?.streetName}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {pendingGoogleAddress?.city}, {pendingGoogleAddress?.state} {pendingGoogleAddress?.zipCode}
              </Typography>
              
              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  Existing units at this address (click to select):
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {existingProperties.filter(p => p.unit).map((property) => (
                    <Chip 
                      key={property.id}
                      size="small" 
                      label={`#${property.unit}`}
                      color="primary"
                      variant="outlined"
                      onClick={() => handleSelectExistingUnit(property)}
                      sx={{ 
                        cursor: 'pointer',
                        '&:hover': { 
                          bgcolor: 'primary.main', 
                          color: 'white',
                          transform: 'scale(1.05)'
                        },
                        transition: 'all 0.2s'
                      }}
                    />
                  ))}
                </Box>
              </Box>
            </Box>
            
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Click on a unit number</strong> to load that property with its appointment history, 
                or enter a new unit number in the form to create a new property.
              </Typography>
            </Alert>
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button 
            onClick={() => {
              setShowMultiUnitDialog(false);
              // Focus on unit field after closing dialog
              setTimeout(() => {
                const unitField = document.querySelector('input[name="unit"]');
                if (unitField) unitField.focus();
              }, 100);
            }} 
            variant="contained" 
            color="primary"
          >
            Enter Unit Number
          </Button>
        </DialogActions>
      </Dialog>
    </Fragment>
  );
};

export default AppointmentCreate;
